import {
  LessonPlayerContextModel,
  LessonPlayerContextProvider
} from '../models/LessonPlayerContext';
import React, { useEffect, useState } from 'react';

import AuthService from '../../shared/services/AuthService';
import LessonLaunchParamsV2 from '../models/LessonLaunchParamsV2';
import LessonPlayerEmbedded from '../components/LessonPlayerEmbedded';
import { useLessonWindow } from '../hooks/useLessonWindow';
import useLessons from '../../content/hooks/useLessons';
import useModulesCompletionTime from '../../dashboard/hooks/useModulesCompletionTime';
import { useStatuscode } from '../hooks/useStatuscode';
import { useUserPortfolio } from '../../externaltool/hooks/useUserPortfolio';

/**
 * Interface for components nested within the lesson portfolio.
 */
export interface ILessonPortfolioComponentProps {
  /** Lesson identifier. */
  lessonkey: number;
  /** Portfolio layer */
  layerkey?: number;
  /** Event for child component to signal a loading message. */
  onContentLoadingMessage?: (message: string | null) => void;
  /** Event for child component to signal an error message. */
  onContentErrorMessage?: (error: string | null) => void;
  /** Event to close or log out of the page. */
  onContentExit?: () => void;
}

/**
 * Component to render a lesson using only authenticated user and lesson key (optional layer).
 * This component renders when lesson is opened through external lesson.
 */
export const LessonPortfolioComponent = (
  props: ILessonPortfolioComponentProps
) => {
  const userkey = AuthService.Userkey;
  const { lessonkey } = props;
  const {
    getUserPortfolio,
    userPortfolioLoading,
    userPortfolioError,
    userPortfolioData
  } = useUserPortfolio();

  const { getStatusCode } = useStatuscode();
  const { getLesson, loadingLesson, loadLessonError, lesson } = useLessons();

  /** Parameters for launching the lesson. */
  const [launchParamsV2, setLaunchParamsV2] =
    useState<LessonLaunchParamsV2>(null);

  const userModulePortfolio = userPortfolioData?.userModulePortfolio;

  /** Set upstream loading message for standard loading screen. */
  useEffect(() => {
    props.onContentLoadingMessage &&
      props.onContentLoadingMessage(
        userPortfolioLoading || loadingLesson ? 'Loading lesson...' : null
      );
  }, [userPortfolioLoading, loadingLesson]);

  /** Set upstream error message if we encounter a problem. */
  useEffect(() => {
    if (props.onContentErrorMessage) {
      const msg =
        userPortfolioLoading || loadingLesson
          ? null
          : userPortfolioError?.graphQLErrors[0]?.message ||
            loadLessonError?.graphQLErrors[0]?.message ||
            (!lesson && 'Error loading lesson') ||
            (!userModulePortfolio?.portfolio && 'Error loading portfolio');
      props.onContentErrorMessage(msg);
    }
  }, [
    userPortfolioLoading,
    userPortfolioData,
    userPortfolioError,
    loadingLesson,
    lesson,
    loadLessonError
  ]);

  // Load data from the services.
  useEffect(() => {
    getUserPortfolio(userkey, lessonkey);
    getLesson(lessonkey);
  }, []);

  const { loadEstimate, estimationTime } = useModulesCompletionTime();

  useEffect(() => {
    if (userModulePortfolio) {
      loadEstimate({
        modulekey: userModulePortfolio.portfolio.modulekey,
        version: userModulePortfolio.portfolio.moduleversion
      });
    }
  }, [userModulePortfolio]);

  useEffect(() => {
    const d = userModulePortfolio;
    const l = lesson;
    if (d && d.module && d.portfolio && l) {
      const lessonWindow = l.type === 'xapi' ? window.open() : null;
      const lessonStatus =
        l.type === 'xapi'
          ? null // TODO: This might be Xapi only, see useXApiLessons?
          : getStatusCode(d.portfolio); //Get lesson status code to show certificate if added
      const params: LessonLaunchParamsV2 = {
        lessonWindow,
        title: d.module.title,
        lessonkey: d.module.modulekey,
        lessonVersion: d.portfolio.moduleversion,
        completedDate: d.portfolio.completed
          ? new Date(d.portfolio.completed)
          : null,
        state: d.portfolio.state,

        lessonType: l.type,
        displaySettings: l.displaySettings,
        completionType: l.behaviorSettings.completionType,
        completionThreshold: l.behaviorSettings.completionThreshold,

        layer: d.portfolio.layer,
        status: lessonStatus,
        hasCert: !!d.module.assets?.certificate?.media,
        timeToComplete: estimationTime
      };
      setLaunchParamsV2(params);
    }
  }, [userPortfolioData, lesson, estimationTime]);

  const handleClose = () => {
    setLaunchParamsV2(null);
    props.onContentExit();
  };

  /** Configuration for lesson player. */
  const lessonPlayerConfig: LessonPlayerContextModel = {
    preview: false,
    coursekey: userModulePortfolio?.module?.coursekey,
    lessonState: {
      answerParams: {
        userkey,
        lessonkey,
        layer: userModulePortfolio?.portfolio?.layer
      },
      state: userModulePortfolio?.portfolio?.state
    }
  };

  const handleCloseMessage = (event?: MessageEvent) => {
    if (!event || event.data === 'lessonComplete') {
      setLaunchParamsV2(null); // * close the lesson player
    }
    handleMessage();
  };

  const { handleMessage } = useLessonWindow(
    launchParamsV2?.lessonWindow,
    handleClose
  );

  return (
    <>
      {launchParamsV2 && (
        <LessonPlayerContextProvider value={lessonPlayerConfig}>
          <LessonPlayerEmbedded
            launchParamsV2={launchParamsV2}
            coursekey={userModulePortfolio?.module.coursekey}
            onClose={handleClose}
            onMessage={handleCloseMessage}
            preview={false}
          />
        </LessonPlayerContextProvider>
      )}
    </>
  );
};
